import { UISref } from "@uirouter/react";
import { noop } from "lodash";
import { FunctionComponent, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { WalletsRoutes } from "../../pages/wallets/routes";
import { AppStore } from "../../store";
import { NotificationActions } from "../../store/notifications/actions";
import { useBrowserWallet } from "../../store/wallets/crypto";
import { colours } from "../../styles/colours";
import { device } from "../../styles/media";
import { formatEther } from "../../utils/utils";
import { Button } from "../button";
import { useCopying } from "../hooks/use-copying";
import { hiddenMobile } from "../layout";

const CryptoWapper = styled.div`
  margin-left: 1em;

  @media ${device.laptop} {
    margin-left: 0;
    margin-right: 1em;
  }
`;

namespace Balance {
  export const Container = styled.div`
    display: flex;
    background: ${colours.darkWhite};
    color: ${colours.black};
    border-radius: 1em;
    flex-direction: column;

    @media ${device.laptop} {
      flex-direction: row;
    }
  `;

  export const Balance = styled.div`
    padding: 0.5em 1em;

    @media ${device.laptop} {
      padding-right: 0.5em;
    }
  `;

  export const Address = styled.div`
    background: ${colours.darkBlueGrey};
    color: ${colours.white};
    padding: 0.5em 1em;
    border-radius: 1em;
  `;

  export const VerifyWrapper = styled(Container)`
    background: ${colours.red};
    color: ${colours.white};
  `;

  export const Verify = styled(Balance)`
    ${hiddenMobile};
    padding: 0.5em 1em;

    @media ${device.laptop} {
      padding-left: 0.5em;
    }
  `;
}

export const WalletBalance: FunctionComponent<{}> = () => {
  const {
    connect,
    verify,
    state,
    balance,
    accounts,
    isVerified,
  } = useBrowserWallet();

  const dispatch = useDispatch();
  const { t } = useTranslation("common");

  const [copying, setCopyingAddress] = useCopying(
    accounts.map(({ coinbase }) => coinbase).getOrElse("")
  );

  useEffect(() => {
    if (copying) {
      dispatch(
        NotificationActions.setNotification({
          title: t("wallet-address-copied"),
          variant: "success",
        })
      );
    }
  }, [copying]);

  const isLoggedIn = useSelector<AppStore, boolean>(
    (store) => store.auth.login.status === "ready"
  );

  const balanceComponent = useMemo(
    () =>
      balance.fold(
        () => null,
        ({ symbol, amount }) => (
          <Balance.Balance>
            {formatEther(amount)} {symbol}
          </Balance.Balance>
        )
      ),
    [balance]
  );

  if (state === "UNAVAILABLE") return null;
  if (state === "LOADING")
    return (
      <CryptoWapper>
        <Button variant="outlined" colour="danger">
          {t("loading")}...
        </Button>
      </CryptoWapper>
    );

  return accounts.fold(
    () => (
      <CryptoWapper>
        <Button variant="outlined" colour="primary" onClick={connect}>
          {t("connect-to-wallet")}
        </Button>
      </CryptoWapper>
    ),
    ({ coinbase }) => {
      const child = (
        <CryptoWapper as="a">
          <Balance.Container>
            {balanceComponent}
            <Balance.VerifyWrapper>
              <Balance.Address
                onClick={isLoggedIn ? noop : () => setCopyingAddress()}
              >
                {coinbase?.substring(0, 5)}...
                {coinbase?.substring(coinbase?.length - 3, coinbase?.length)}
              </Balance.Address>
              {isVerified || !isLoggedIn ? null : (
                <Balance.Verify
                  onClick={(e) => {
                    e.preventDefault();
                    verify();
                  }}
                >
                  {t("verify")}
                </Balance.Verify>
              )}
            </Balance.VerifyWrapper>
          </Balance.Container>
        </CryptoWapper>
      );

      return isLoggedIn ? (
        <UISref to={WalletsRoutes.names.walletList}>{child}</UISref>
      ) : (
        child
      );
    }
  );
};
